/**
 * WII Benchmarking Data
 * 
 * Aggregate and simulated data for Workforce Intelligence Index benchmarking
 * Future-ready structure for organization-level and institutional dashboards
 */

import { SEVEN_FORCES_ARRAY, WII_LEVELS } from './workforceIntelligence';

// ============================================
// AGGREGATE BENCHMARK DATA (Simulated)
// ============================================

/**
 * Global benchmark data structure
 * In production, this would come from backend aggregation
 */
export const GLOBAL_BENCHMARK_DATA = {
  // Metadata
  lastUpdated: "2026-03-24T00:00:00Z",
  dataSource: "Global Workforce Observatory",
  sampleSize: 1247,
  
  // Overall WII Statistics
  overallStats: {
    averageWII: 54.3,
    medianWII: 52,
    standardDeviation: 18.7,
    minWII: 12,
    maxWII: 98
  },
  
  // Force-level averages
  forceAverages: {
    capability_acceleration: 58.2,
    workforce_autonomy: 52.4,
    trust_alignment: 61.8,
    learning_transformation: 45.6,
    opportunity_fluidity: 56.3,
    quality_discipline: 54.1,
    institutional_coherence: 48.9
  },
  
  // Distribution by WII level
  levelDistribution: {
    low: { count: 312, percentage: 25.0 },      // 0-40
    moderate: { count: 686, percentage: 55.0 }, // 41-70
    high: { count: 249, percentage: 20.0 }      // 71-100
  },
  
  // Readiness level distribution
  readinessDistribution: {
    basic: { count: 312, percentage: 25.0 },
    developing: { count: 686, percentage: 55.0 },
    advanced: { count: 249, percentage: 20.0 }
  },
  
  // Industry breakdown (future-ready)
  industryBreakdown: {
    technology: { averageWII: 62.4, count: 312 },
    healthcare: { averageWII: 51.2, count: 187 },
    finance: { averageWII: 58.7, count: 156 },
    education: { averageWII: 48.3, count: 234 },
    manufacturing: { averageWII: 44.8, count: 143 },
    retail: { averageWII: 46.2, count: 98 },
    government: { averageWII: 52.1, count: 67 },
    other: { averageWII: 53.4, count: 50 }
  },
  
  // Regional breakdown (future-ready)
  regionalBreakdown: {
    northAmerica: { averageWII: 56.2, count: 423 },
    europe: { averageWII: 54.8, count: 312 },
    asiaPacific: { averageWII: 52.1, count: 287 },
    latinAmerica: { averageWII: 48.7, count: 125 },
    middleEastAfrica: { averageWII: 47.3, count: 100 }
  },
  
  // Trend data (last 6 months - future-ready)
  monthlyTrends: [
    { month: "Oct 2025", averageWII: 51.2, submissions: 156 },
    { month: "Nov 2025", averageWII: 52.8, submissions: 189 },
    { month: "Dec 2025", averageWII: 53.4, submissions: 178 },
    { month: "Jan 2026", averageWII: 54.1, submissions: 234 },
    { month: "Feb 2026", averageWII: 54.9, submissions: 267 },
    { month: "Mar 2026", averageWII: 55.3, submissions: 223 }
  ]
};

// ============================================
// INSIGHTS GENERATION
// ============================================

/**
 * Generate insights from benchmark data
 */
export function generateBenchmarkInsights(data = GLOBAL_BENCHMARK_DATA) {
  const insights = [];
  
  // Find highest and lowest forces
  const forceEntries = Object.entries(data.forceAverages);
  const sortedForces = forceEntries.sort((a, b) => b[1] - a[1]);
  const highestForce = sortedForces[0];
  const lowestForce = sortedForces[sortedForces.length - 1];
  
  // Get force display names
  const getForceDisplayName = (forceId) => {
    const force = SEVEN_FORCES_ARRAY.find(f => f.id === forceId);
    return force?.name || forceId;
  };
  
  // Insight 1: Highest force
  insights.push({
    type: 'strength',
    icon: 'trending-up',
    title: 'Top Performing Force',
    description: `${getForceDisplayName(highestForce[0])} shows the highest average score (${highestForce[1].toFixed(1)}) across all users, indicating strong alignment and trust foundations.`,
    metric: highestForce[1].toFixed(1),
    category: 'force-analysis'
  });
  
  // Insight 2: Lowest force
  insights.push({
    type: 'gap',
    icon: 'alert-triangle',
    title: 'Development Priority',
    description: `${getForceDisplayName(lowestForce[0])} shows the lowest average score (${lowestForce[1].toFixed(1)}), representing the greatest opportunity for workforce development investment.`,
    metric: lowestForce[1].toFixed(1),
    category: 'force-analysis'
  });
  
  // Insight 3: Distribution insight
  const majorityLevel = Object.entries(data.levelDistribution)
    .sort((a, b) => b[1].percentage - a[1].percentage)[0];
  
  insights.push({
    type: 'distribution',
    icon: 'pie-chart',
    title: 'Workforce Distribution',
    description: `${majorityLevel[1].percentage}% of the workforce is at ${majorityLevel[0]} readiness level, suggesting ${majorityLevel[0] === 'moderate' ? 'significant potential for advancement' : majorityLevel[0] === 'low' ? 'urgent need for foundational development' : 'strong workforce capability'}.`,
    metric: `${majorityLevel[1].percentage}%`,
    category: 'distribution'
  });
  
  // Insight 4: Trend insight
  const recentTrend = data.monthlyTrends.slice(-3);
  const trendDirection = recentTrend[recentTrend.length - 1].averageWII > recentTrend[0].averageWII;
  
  insights.push({
    type: trendDirection ? 'positive' : 'negative',
    icon: trendDirection ? 'arrow-up-right' : 'arrow-down-right',
    title: 'WII Trend',
    description: `Average WII has ${trendDirection ? 'increased' : 'decreased'} by ${Math.abs(recentTrend[recentTrend.length - 1].averageWII - recentTrend[0].averageWII).toFixed(1)} points over the last 3 months, indicating ${trendDirection ? 'positive workforce intelligence growth' : 'need for intervention'}.`,
    metric: `${trendDirection ? '+' : '-'}${Math.abs(recentTrend[recentTrend.length - 1].averageWII - recentTrend[0].averageWII).toFixed(1)}`,
    category: 'trend'
  });
  
  // Insight 5: Industry insight
  const topIndustry = Object.entries(data.industryBreakdown)
    .sort((a, b) => b[1].averageWII - a[1].averageWII)[0];
  
  insights.push({
    type: 'industry',
    icon: 'building',
    title: 'Leading Industry',
    description: `${topIndustry[0].charAt(0).toUpperCase() + topIndustry[0].slice(1)} sector leads with an average WII of ${topIndustry[1].averageWII}, setting the benchmark for AI integration and workforce readiness.`,
    metric: topIndustry[1].averageWII.toFixed(1),
    category: 'industry'
  });
  
  return insights;
}

// ============================================
// COMPARISON FUNCTIONS
// ============================================

/**
 * Compare user WII to global benchmark
 */
export function compareToGlobalBenchmark(userWII, data = GLOBAL_BENCHMARK_DATA) {
  const diff = userWII - data.overallStats.averageWII;
  const percentile = calculatePercentile(userWII, data);
  
  return {
    userWII,
    globalAverage: data.overallStats.averageWII,
    difference: diff,
    percentile,
    comparison: diff > 0 ? 'above' : diff < 0 ? 'below' : 'at',
    description: diff > 0 
      ? `Your WII is ${diff.toFixed(1)} points above the global average`
      : diff < 0
      ? `Your WII is ${Math.abs(diff).toFixed(1)} points below the global average`
      : `Your WII matches the global average`
  };
}

/**
 * Calculate approximate percentile
 */
function calculatePercentile(score, data) {
  // Simplified percentile calculation based on distribution
  const { low, moderate, high } = data.levelDistribution;
  
  if (score <= 40) {
    return Math.round((score / 40) * low.percentage);
  } else if (score <= 70) {
    return Math.round(low.percentage + ((score - 40) / 30) * moderate.percentage);
  } else {
    return Math.round(low.percentage + moderate.percentage + ((score - 70) / 30) * high.percentage);
  }
}

/**
 * Compare user force scores to global averages
 */
export function compareForceScoresToBenchmark(userForceScores, data = GLOBAL_BENCHMARK_DATA) {
  if (!userForceScores) return [];
  
  return SEVEN_FORCES_ARRAY.map(force => {
    const userScore = userForceScores[force.id];
    const globalAverage = data.forceAverages[force.id];
    const diff = userScore !== null ? userScore - globalAverage : null;
    
    return {
      forceId: force.id,
      forceName: force.name,
      forceShortName: force.shortName,
      forceColor: force.color,
      userScore,
      globalAverage,
      difference: diff,
      comparison: diff > 0 ? 'above' : diff < 0 ? 'below' : 'at'
    };
  });
}

// ============================================
// ORGANIZATION-LEVEL TEMPLATES (Future-Ready)
// ============================================

export const ORGANIZATION_BENCHMARK_TEMPLATE = {
  organizationId: null,
  organizationName: null,
  industry: null,
  employeeCount: null,
  
  // Aggregate WII data
  aggregateWII: {
    average: null,
    median: null,
    participationRate: null,
    totalAssessments: null
  },
  
  // Force averages for organization
  forceAverages: {
    capability_acceleration: null,
    workforce_autonomy: null,
    trust_alignment: null,
    learning_transformation: null,
    opportunity_fluidity: null,
    quality_discipline: null,
    institutional_coherence: null
  },
  
  // Department breakdown
  departmentBreakdown: [],
  
  // Comparison to industry benchmark
  industryComparison: null,
  
  // Trends
  monthlyTrends: [],
  
  // Generated insights
  insights: []
};

export const INSTITUTIONAL_BENCHMARK_TEMPLATE = {
  institutionId: null,
  institutionType: null, // government, sector, region
  scope: null,
  
  // Aggregate data
  totalOrganizations: null,
  totalAssessments: null,
  averageWII: null,
  
  // Benchmarks
  benchmarks: {
    national: null,
    regional: null,
    sectorSpecific: null
  },
  
  // Policy recommendations
  policyRecommendations: [],
  
  // Strategic priorities
  strategicPriorities: []
};

// ============================================
// EXPORTS
// ============================================

export default {
  GLOBAL_BENCHMARK_DATA,
  generateBenchmarkInsights,
  compareToGlobalBenchmark,
  compareForceScoresToBenchmark,
  ORGANIZATION_BENCHMARK_TEMPLATE,
  INSTITUTIONAL_BENCHMARK_TEMPLATE
};
